import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (pollCount >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await fetch(`${API}/checkout/status/${sessionId}`);
      if (res.ok) {
        const data = await res.json();
        setPaymentDetails(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Payment status check error:', error);
      setStatus('error');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080B14' }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Verifying Payment...</h2>
          <p className="text-gray-400">Please wait while we confirm your payment.</p>
        </motion.div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 pb-20" style={{ background: '#080B14' }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto px-6"
        >
          <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6 border border-green-500/20 shadow-[0_0_20px_rgba(16,185,129,0.2)]">
            <CheckCircle2 className="w-10 h-10 text-green-400" />
          </div>

          <h1 className="text-3xl font-bold text-white mb-3" data-testid="payment-success-title">
            Payment Successful!
          </h1>
          <p className="text-gray-400 mb-8 leading-relaxed">
            Thank you for your purchase. Your subscription is now active and you can 
            start using our services immediately.
          </p>

          {paymentDetails && (
            <div 
              className="p-6 rounded-xl mb-8 text-left border"
              style={{ background: '#0D1224', borderColor: 'rgba(99, 130, 255, 0.12)' }}
            >
              <h3 className="text-white font-medium mb-4">Payment Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Amount</span>
                  <span className="text-white font-medium">
                    ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Status</span>
                  <span className="text-green-400 font-medium">Paid</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Reference</span>
                  <span className="text-white font-mono text-xs">{sessionId?.slice(0, 20)}...</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/dashboard">
              <Button 
                className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:opacity-95 text-white font-semibold px-8 border-none shadow-[0_4px_12px_rgba(59,130,246,0.2)]"
                data-testid="go-to-dashboard"
              >
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/bookings">
              <Button 
                variant="outline"
                className="border-blue-500/20 text-white bg-transparent hover:bg-blue-500/5"
              >
                Schedule Service
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  // Error states
  return (
    <div className="min-h-screen flex items-center justify-center pt-20 pb-20" style={{ background: '#080B14' }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-md mx-auto px-6"
      >
        <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6 border border-red-500/25 shadow-[0_0_20px_rgba(239,68,68,0.2)]">
          <span className="text-4xl">⚠️</span>
        </div>

        <h1 className="text-2xl font-bold text-white mb-3">
          {status === 'timeout' && 'Verification Timeout'}
          {status === 'expired' && 'Session Expired'}
          {status === 'error' && 'Something Went Wrong'}
        </h1>
        <p className="text-gray-400 mb-8 leading-relaxed">
          {status === 'timeout' && 'We couldn\'t verify your payment in time. Please check your email for confirmation.'}
          {status === 'expired' && 'This payment session has expired. Please try again.'}
          {status === 'error' && 'We encountered an error verifying your payment. Please contact support.'}
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/pricing">
            <Button className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:opacity-95 text-white font-semibold border-none shadow-[0_4px_12px_rgba(59,130,246,0.2)]">
              Try Again
            </Button>
          </Link>
          <Link to="/contact">
            <Button variant="outline" className="border-blue-500/20 text-white bg-transparent hover:bg-blue-500/5">
              Contact Support
            </Button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
